<template>
    <div class="tw-bg-white tw-border tw-border-solid tw-border-gray-200 tw-p-2 tw-rounded">
        <div class="tw-flex tw-items-center tw-gap-2">
            <p class="tw-font-bold tw-text-lg tw-px-2">Inventory</p>
            <div v-if="inStock <= product?.stock_alert" class="tw-animate-pulse tw-flex tw-items-center tw-text-xs tw-gap-1 tw-px-1 tw-py-0.5 tw-bg-red-100 tw-text-rose-500 tw-rounded">
                <icon icon="si:alert-duotone" />
                <span>Low stock</span>
            </div>
        </div>

        <div v-if="loading" class="tw-flex tw-gap-1 tw-mt-2 ">
            <div class="tw-h-2 tw-rounded-s-md" :class="['tw-bg-gray-100']"
                :style="{ width: '20%' }"></div>
            <div class="tw-h-2 tw-rounded-mdx" :class="['tw-bg-gray-200']"
                :style="{ width: '60%' }"></div>
            <div class="tw-h-2 tw-rounded-e-md" :class="['tw-bg-gray-100']"
                :style="{ width: '20%' }"></div>
        </div>

        <div v-if="!loading" class="tw-flex tw-gap-1 tw-mt-2 ">
            <div class="tw-h-2 tw-rounded-l-md" :class="['tw-bg-cyan-500']"
                :style="{ width: getPercentage(inStock) + '%' }"></div>
            <div class="tw-h-2" :class="['tw-bg-violet-400']" :style="{ width: getPercentage(shipped) + '%' }"></div>
            <div class="tw-h-2 tw-rounded-r-md" :class="['tw-bg-emerald-400']"
                :style="{ width: getPercentage(delivered) + '%' }"></div>
        </div>

        <div v-if="!loading" class="tw-flex tw-items-center tw-gap-2 tw-mt-4 tw-text-sm tw-flex-wrap">
            <div class="tw-flex tw-items-center tw-gap-1 tw-bg-black/10 tw-rounded tw-px-2 tw-py-0.5">
                <span class="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-black"></span>
                <div class="tw-flex tw-items-center tw-gap-1">
                    <strong class="tw-text-xs">{{ totalQuantity }}</strong>
                    <span class="tw-text-xs">{{ $t('common.total') }}</span>
                </div>
            </div>
            <div class="tw-flex tw-items-center tw-gap-1 tw-bg-cyan-500/10 tw-rounded tw-px-2 tw-py-0.5">
                <span class="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-cyan-500"></span>
                <div class="tw-flex tw-items-center tw-gap-1">
                    <strong class="tw-text-xs">{{ inStock }}</strong>
                    <span class="tw-text-xs">{{ $t('common.in-stock') }}</span>
                </div>
            </div>
            <div class="tw-flex tw-items-center tw-gap-1 tw-bg-violet-500/10 tw-rounded tw-px-2 tw-py-0.5">
                <span class="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-violet-400"></span>
                <div class="tw-flex tw-items-center tw-gap-1">
                    <strong class="tw-text-xs">{{ shipped }}</strong>
                    <span class="tw-text-xs">{{ $t('common.shipped') }}</span>
                </div>
            </div>
            <div class="tw-flex tw-items-center tw-gap-1 tw-bg-emerald-500/10 tw-rounded tw-px-2 tw-py-0.5">
                <span class="tw-w-2 tw-h-2 tw-rounded-full tw-bg-emerald-400"></span>
                <div class="tw-flex tw-items-center tw-gap-1">
                    <strong class="tw-text-xs">{{ delivered }}</strong>
                    <span class="tw-text-xs">{{ $t('common.delivered') }}</span>
                </div>
            </div>
        </div>

        <div v-if="loading" class="tw-flex tw-items-center tw-gap-2 tw-mt-4 tw-text-sm">
            <div class="tw-flex tw-items-center tw-gap-1 tw-bg-black/10 tw-rounded tw-px-2 tw-py-0.5">
                <span class="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-black"></span>
                <div class="tw-flex tw-items-center tw-gap-1">
                    <icon icon="eos-icons:three-dots-loading" />
                    <span class="tw-text-xs">{{ $t('common.total') }}</span>
                </div>
            </div>
            <div class="tw-flex tw-items-center tw-gap-1 tw-bg-cyan-500/10 tw-rounded tw-px-2 tw-py-0.5">
                <span class="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-cyan-500"></span>
                <div class="tw-flex tw-items-center tw-gap-1">
                    <icon icon="eos-icons:three-dots-loading" />
                    <span class="tw-text-xs">{{ $t('common.in-stock') }}</span>
                </div>
            </div>
            <div class="tw-flex tw-items-center tw-gap-1 tw-bg-violet-500/10 tw-rounded tw-px-2 tw-py-0.5">
                <span class="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-violet-400"></span>
                <div class="tw-flex tw-items-center tw-gap-1">
                    <icon icon="eos-icons:three-dots-loading" />
                    <span class="tw-text-xs">{{ $t('common.shipped') }}</span>
                </div>
            </div>
            <div class="tw-flex tw-items-center tw-gap-1 tw-bg-emerald-500/10 tw-rounded tw-px-2 tw-py-0.5">
                <span class="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-emerald-400"></span>
                <div class="tw-flex tw-items-center tw-gap-1">
                    <icon icon="eos-icons:three-dots-loading" />
                    <span class="tw-text-xs">{{ $t('common.delivered') }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { inventory } from '@/api/products';
import { computed, ref, inject } from 'vue';

const loading = ref(true);
const id = inject('id');
const product = inject('product');

const inStock = ref(20);
const shipped = ref(4);
const delivered = ref(18);

const totalQuantity = computed(() => inStock.value + shipped.value + delivered.value);

const getPercentage = (count) => {
    if(count <= 0) return 0;
    return totalQuantity.value > 0 ? (count / totalQuantity.value) * 100 : 0;
};

const getData = async () => {
    loading.value = true;

    await inventory(id)
    .then(
        res => {
            if(res.data.code == 'SUCCESS') {
                inStock.value = res.data.data.available_quantity;
                shipped.value = res.data.data.shipped_quantity;
                delivered.value = res.data.data.delivered_quantity;
            }
        }
    )

    loading.value = false;
}

getData();

</script>